import { API } from "@infra/api";
import { AgrianGeoFetcher } from "@domain/Integrations/Agrian";
import { ProagricaGeoFetcher } from "@domain/Integrations/Proagrica";
import type { IAgrianApi } from "@ports/integrations/IAgrianApi";
import type { IAgrianGeoFetcher } from "@ports/integrations/IAgrianGeoFetcher";
import type { IProagricaApi } from "@ports/integrations/IProagricaApi";
import type { IProagricaGeoFetcher } from "@ports/integrations/IProagricaGeoFetcher";
import { makeAgrianUseCases, makeProagricaUseCases } from "@app/integrations";

const agrianApi: IAgrianApi = {
	getOrganizations: (type, force) => API.Integrations.Agrian.getOrganizations(type, force),
	getOrganizationsByIds: (ids, type, force) => API.Integrations.Agrian.getOrganizationsByIds(ids, type, force),
	getGrowers: (orgIds, type, force) => API.Integrations.Agrian.getGrowers(orgIds, type, force),
	getFarms: (growerIds, type, force) => API.Integrations.Agrian.getFarms(growerIds, type, force),
	getFields: (farmIds, type, force) => API.Integrations.Agrian.getFields(farmIds, type, force),
	getSampleEvents: (fieldIds, type, force) => API.Integrations.Agrian.getSampleEvents(fieldIds, type, force),
	getTargetEvents: (fieldIds, type) => API.Integrations.Agrian.getTargetEvents(fieldIds, type),
	getZones: (fieldIds, type) => API.Integrations.Agrian.getZones(fieldIds, type),
};

const geoFetcher: IAgrianGeoFetcher = {
	getBoundaryGeoJSON: (records) => AgrianGeoFetcher.getBoundaryGeoJSON(records),
	getSampleEventCentroidsGeoJSON: (event) => AgrianGeoFetcher.getSampleEventCentroidsGeoJSON(event),
	getSampleEventCoresGeoJSON: (event) => AgrianGeoFetcher.getSampleEventCoresGeoJSON(event),
	getSampleEventZonesGeoJSON: (event) => AgrianGeoFetcher.getSampleEventZonesGeoJSON(event),
	getTargetEventCentroidsGeoJSON: (event) => AgrianGeoFetcher.getTargetEventCentroidsGeoJSON(event),
	getTargetEventZonesGeoJSON: (event) => AgrianGeoFetcher.getTargetEventZonesGeoJSON(event),
	getZoneMapGeoJSON: (zones) => AgrianGeoFetcher.getZoneMapGeoJSON(zones),
};

const agrianUseCases = makeAgrianUseCases({ agrianApi, geoFetcher });

const proagricaApi: IProagricaApi = {
	login: (returnUrl) => API.Integrations.Proagrica.login(returnUrl),
	refreshToken: () => API.Integrations.Proagrica.refreshToken(),
	getGrowers: (force) => API.Integrations.Proagrica.getGrowers(force),
	getFarm: (farm_uuid, sync_id) => API.Integrations.Proagrica.getFarm(farm_uuid, sync_id),
	getFarmsForGrowers: (growerIds, sync_id, force) =>
		API.Integrations.Proagrica.getFarmsForGrowers(growerIds, sync_id, force),
	getFieldsForGrowerFarms: (grower, farms, sync_id, force) =>
		API.Integrations.Proagrica.getFieldsForGrowerFarms(grower, farms, sync_id, force),
	getSamplingItems: (field_uuid, sync_id) => API.Integrations.Proagrica.getSamplingItems(field_uuid, sync_id),
};

const proagricaGeoFetcher: IProagricaGeoFetcher = {
	getBoundaryGeoJSON: (records) => ProagricaGeoFetcher.getBoundaryGeoJSON(records),
	getZoneOrPointsGeoJSON: (item) => ProagricaGeoFetcher.getZoneOrPointsGeoJSON(item),
};

const proagricaUseCases = makeProagricaUseCases({ proagricaApi, geoFetcher: proagricaGeoFetcher });

/**
 * Bound integration use cases. Sub-namespaces match the integration name —
 * `useIntegrationsUseCases().agrian` and `.proagrica` give consumers a
 * single entry point per integration.
 */
export const integrationsUseCases = {
	agrian: agrianUseCases,
	proagrica: proagricaUseCases,
};

export type IntegrationsUseCases = typeof integrationsUseCases;
